import { z } from 'zod';
import { ObjectIdSchema } from '../../../../shared/object-id.schema';
/**
 * Lifecycle of a tenant clone job — one axis, exhaustively switchable.
 *
 * There is deliberately no `succeeded`: a clone that finishes deletes its job
 * document, and the structured transition log line is the permanent record.
 * Every job this endpoint returns is therefore unfinished business, and an
 * empty list means "nothing outstanding" rather than "nothing ever ran".
 *
 * The two halves an admin has to tell apart are whether the TENANT still needs
 * a decision or was kept:
 *
 * - `running` — the detached clone is still working; its heartbeat is fresh.
 * - `interrupted` — no heartbeat for the staleness window, so the reaper parked
 *   it. A timer cannot tell dead work from slow work (a 300-user import is
 *   legitimately ~15 min of silence), so parking is ALL the reaper does: it
 *   never drops a database and never deletes a tenant. The half-built tenant
 *   waits, inactive, for an explicit discard.
 * - `failed` — the clone caught an error and rolled back what it could. The
 *   tenant is inactive or already gone.
 * - `importFailed` — the clone SUCCEEDED and its tenant is active and usable;
 *   only the demo Zitadel import did not run. Nothing to roll back, so the
 *   discard endpoint refuses it and the only action is a dismiss.
 * - `completed` — the clone succeeded and the tenant was handed over (active,
 *   ownership released), but clearing the job afterwards failed. The clone is
 *   fine; only this record is leftover bookkeeping. Dismiss-only, like
 *   `importFailed`.
 *
 * `importFailed` and `completed` are statuses rather than a boolean riding
 * alongside one: a flag would force every consumer to decode two fields, and a
 * flag can be set on a status it contradicts.
 */ export const TenantCloneJobStatusSchema = z.enum([
    'running',
    'interrupted',
    'failed',
    'importFailed',
    'completed'
]);
/**
 * The single home of that partition, derived from the one stored axis rather
 * than stored alongside it — which is what keeps it from contradicting the
 * status it groups, exactly how the `sweepFailed` boolean it replaced went
 * wrong.
 *
 * An exhaustive `switch` on purpose: a sixth status has to be classified here
 * or the build fails, instead of falling silently through a badge into the
 * wrong reading.
 */ export function tenantCloneJobDisposition(status) {
    switch(status){
        case 'running':
            return 'running';
        case 'interrupted':
        case 'failed':
            return 'needsDecision';
        case 'importFailed':
        case 'completed':
            return 'tenantKept';
    }
}
/**
 * Coarse progress marker for the admin's tooltip — never a resume point.
 *
 * Jobs are deliberately not resumable: resuming a demo clone would require
 * persisting the plaintext demo password.
 */ export const TenantCloneJobPhaseSchema = z.enum([
    'copying',
    'provisioning',
    'importing'
]);
/**
 * The compensating steps a discard runs, in the order it runs them.
 *
 * Order is load-bearing, and so is reporting each step separately: memberships
 * are global registry state that outlives the tenant database, and a Zitadel
 * org has to be deactivated while the tenant row still exists to name it.
 */ export const TenantCloneCleanupStepSchema = z.enum([
    'revokeMemberships',
    'deactivateZitadelOrg',
    'dropDatabase',
    'deleteTenantRow'
]);
/**
 * `skipped` is not a failure: it marks a step whose precondition never existed
 * (no Zitadel org was persisted for this clone) or that an earlier failure made
 * unsafe to attempt.
 */ export const TenantCloneCleanupOutcomeSchema = z.enum([
    'succeeded',
    'skipped',
    'failed'
]);
export const TenantCloneCleanupStepResultSchema = z.object({
    step: TenantCloneCleanupStepSchema,
    outcome: TenantCloneCleanupOutcomeSchema
});
/**
 * What a discard actually managed to undo.
 *
 * Each compensating step is best-effort so one failure does not strand the
 * others — but the outcome is recorded rather than swallowed. A discard that
 * left anything behind keeps its job visible with the evidence attached,
 * instead of disappearing and implying a clean rollback that did not happen.
 *
 * ONE writer, and therefore one meaning: `POST …/discard`. The clone's own
 * compensating rollback deliberately writes nothing here — its per-step
 * outcomes go to the `failed` transition log line — so the presence of this
 * field says "an admin discard was attempted", never "a clone rolled back".
 * Whether anything is LEFT OVER is `steps.some(s => s.outcome === 'failed')`;
 * there is deliberately no summary field, because a derived summary is one more
 * thing that can drift out of sync with the steps it summarizes.
 */ export const TenantCloneCleanupStatusSchema = z.object({
    attemptedAt: z.iso.datetime(),
    /** In compensation order, one entry per `TenantCloneCleanupStepSchema`. */ steps: z.array(TenantCloneCleanupStepResultSchema)
});
/**
 * One unfinished tenant clone, as the superadmin tenant list sees it.
 *
 * Deliberately NO `lastError`: the job document stores one for operators, but
 * stored clone errors carry PII — a Zitadel API error body echoes the request
 * (the username IS the email) and a Mongo E11000 message embeds the duplicate
 * key value, which is exactly why the sweep importer redacts email-like tokens
 * before it stores anything. Raw internal errors do not belong on the wire at
 * all, and neither does the job's stored `errorCode`: `status` alone selects
 * the message the admin reads, and the detail stays in the backend logs.
 */ export const TenantCloneJobSchema = z.object({
    jobId: ObjectIdSchema,
    /** The CLONE's tenant — the row the POST created, never the source. */ tenantId: ObjectIdSchema,
    /**
   * The tenant being copied FROM. On the wire because the client has to disable
   * a running clone's row actions on BOTH sides: `delete-tenant` refuses for
   * the source as well as the target, and a button that only fails after the
   * destructive confirm dialog is worse than a disabled one.
   */ sourceTenantId: ObjectIdSchema,
    /**
   * Denormalized off the tenant row on purpose: a discard deletes that row, and
   * the admin still has to be told which clone it was that failed.
   */ tenantName: z.string(),
    tenantSlug: z.string(),
    status: TenantCloneJobStatusSchema,
    phase: TenantCloneJobPhaseSchema,
    cleanupStatus: TenantCloneCleanupStatusSchema.optional(),
    startedAt: z.iso.datetime()
});
/**
 * List Clone Jobs Response Schema
 * GET /api/v2/tenant-clone-jobs
 *
 * Its own endpoint rather than a field on `list-tenants`: the tenant list is
 * shared by five unrelated screens and fans out two per-tenant database counts,
 * so polling it every few seconds to watch one clone would be a 2xN fan-out for
 * everyone. This returns unfinished jobs and nothing else, and the client merges
 * them into the tenant rows.
 */ export const ListCloneJobsResponseSchema = z.object({
    jobs: z.array(TenantCloneJobSchema)
});

//# sourceMappingURL=list-clone-jobs.schema.js.map